import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import {
  supabase,
  getSafeUser,
  getSafeSession,
} from "@/store/shared/supabase";
import type { PermissionMap } from "@/store/permissions/catalog";
import type { AccountRole, AccountStatus, SessionUser } from "./types";

export type LoginResult =
  | { ok: true; user: SessionUser }
  | { ok: false; error: string };

interface SessionContextValue {
  session: SessionUser | null;
  isAuthenticated: boolean;
  /** Chargement initial de la session Auth (à utiliser par les gardes de routes). */
  loading: boolean;
  /** Connexion par e-mail / mot de passe (Supabase Auth). */
  login: (email: string, password: string, remember?: boolean) => Promise<LoginResult>;
  /** Recharge le profil ImmoBF depuis la base (RPC mon_profil). */
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function metaNomComplet(user: User | null): string {
  const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
  const candidate = meta.nom_complet ?? meta.full_name ?? meta.name;
  if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
  return (user?.email ?? "").split("@")[0];
}

function decouperNom(full: string): { prenom: string; nom: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { prenom: "", nom: "" };
  if (parts.length === 1) return { prenom: parts[0], nom: "" };
  return { prenom: parts[0], nom: parts.slice(1).join(" ") };
}

function fonctionPourRole(role: AccountRole): string {
  switch (role) {
    case "dg_proprietaire":
      return "Propriétaire";
    case "dg_agence":
      return "Directeur d'agence";
    case "employe_agence":
      return "Employé d'agence";
    case "super_admin":
      return "Super administrateur";
    default:
      return "";
  }
}

function traduireErreurAuth(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("invalid login") || m.includes("invalid credentials")) {
    return "E-mail ou mot de passe incorrect.";
  }
  if (m.includes("email not confirmed")) {
    return "Votre e-mail n'a pas encore été confirmé. Vérifiez votre boîte de réception.";
  }
  if (m.includes("email rate limit") || m.includes("over_email_send_rate_limit")) {
    return "Impossible d'envoyer l'e-mail pour le moment : la limite d'envoi vient d'être atteinte. Patientez environ une heure avant de réessayer.";
  }
  if (m.includes("rate limit") || m.includes("too many") || m.includes("over_request_rate_limit")) {
    return "Trop de tentatives en peu de temps. Patientez quelques minutes avant de réessayer.";
  }
  if (m.includes("user already registered")) {
    return "Un compte existe déjà avec cette adresse e-mail.";
  }
  return "Connexion impossible. Vérifiez vos identifiants et réessayez.";
}

/**
 * Détermine la raison RÉELLE de blocage d'un compte en attente à partir du
 * résultat (ou de l'erreur) de l'offre de lancement (`demarrer_essai_si_eligible`).
 * Objectif : ne JAMAIS laisser un compte bloqué sans explication. À défaut de
 * raison serveur exploitable, on renvoie une cause explicite (erreur technique,
 * activation incomplète…) au lieu d'un message générique trompeur.
 */
function resoudreBlocageEssai(
  essai: EssaiRpc | null,
  erreur: string | null
): { raison: string; code: string } {
  // Le serveur a explicitement refusé l'octroi : sa raison est la vérité.
  if (essai && essai.demarre === false) {
    return {
      code: essai.raison_code ?? "non_eligible",
      raison: essai.raison || "L'offre de lancement n'a pas pu être attribuée à ce compte.",
    };
  }

  // Le serveur a accordé l'essai mais le compte n'est pas encore lisible comme
  // actif : on l'explique au lieu d'un message générique trompeur.
  if (essai && essai.demarre === true) {
    return {
      code: "activation_incomplete",
      raison:
        "Votre essai de 6 mois a bien été enregistré, mais l'activation de votre compte n'est pas encore effective. Rechargez la page ou reconnectez-vous dans quelques instants.",
    };
  }

  // La RPC a renvoyé une erreur (auparavant avalée en silence) : on expose la cause réelle.
  if (erreur) {
    return {
      code: "erreur_activation",
      raison:
        "L'activation automatique de votre essai a échoué. Réessayez ; si le blocage persiste, contactez le support. Détail : " +
        erreur,
    };
  }

  // Aucun retour exploitable : on l'affirme clairement plutôt que de laisser un vide.
  return {
    code: "activation_indisponible",
    raison:
      "Votre compte est en attente et l'attribution automatique de l'essai n'a pas pu être vérifiée. Rechargez la page ; si le blocage persiste, contactez le support.",
  };
}

interface EtatCompteRpc {
  authentifie?: boolean;
  compte_actif?: boolean;
  raison?: string;
  role?: AccountRole;
  statut?: AccountStatus;
}

/** Résultat structuré renvoyé par `demarrer_essai_si_eligible()`. */
interface EssaiRpc {
  demarre?: boolean;
  raison_code?: string;
  raison?: string;
}

interface MonProfilRpc {
  connecte?: boolean;
  compte?: {
    id: string;
    nom_complet?: string;
    email?: string;
    telephone?: string | null;
    role: AccountRole;
    statut: AccountStatus;
    type_compte?: string | null;
  };
  agence?: { id: string; nom?: string } | null;
  proprietaire?: { id: string } | null;
  employe?: {
    permissions?: unknown;
    agence_id?: string | null;
    proprietaire_id?: string | null;
  } | null;
}

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */

/**
 * Session réelle basée sur Supabase Auth.
 * - La session Auth est gérée par le SDK Supabase (persistance incluse).
 * - Le profil ImmoBF est chargé via les RPC sécurisées `compte_authentifie`
 *   (état du compte, y compris blocage) et `mon_profil` (détails du compte).
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (authUser: User | null): Promise<SessionUser | null> => {
    let user = authUser;
    if (!user) {
      // Lecture sans exception : un refresh token périmé purge la session locale
      // et renvoie `null` au lieu de planter l'écran.
      user = await getSafeUser();
    }
    if (!user) return null;

    // Offre de lancement : octroi AUTOMATIQUE et idempotent de l'essai gratuit
    // de 6 mois (activation du compte + accès complet). Entièrement côté serveur :
    // la fonction SECURITY DEFINER recalcule tout à partir de auth.uid() et ne fait
    // jamais confiance au rôle, au statut ou à la formule envoyés par le navigateur.
    //
    // Le résultat est CONSERVÉ : si l'essai n'a pas pu être attribué (offre déjà
    // utilisée, paiement en attente, etc.), sa raison réelle est affichée sur
    // l'écran de blocage au lieu d'un message générique trompeur.
    let essai: EssaiRpc | null = null;
    let essaiErreur: string | null = null;
    try {
      const { data, error } = await supabase.rpc("demarrer_essai_si_eligible");
      if (error) {
        essaiErreur = error.message;
      } else {
        essai = (data as EssaiRpc | null) ?? null;
      }
    } catch (error) {
      /* L'erreur n'est plus avalée : elle est conservée pour être affichée. */
      essaiErreur = error instanceof Error ? error.message : String(error);
    }

    // Notifications d'expiration de l'essai (J-30 / J-7 / J-1) : la génération
    // est assurée par NotificationsProvider (RPC `generer_notifications_essai`),
    // bornée au compte connecté et couverte par l'index unique d'anti-doublon.
    // L'ancien appel global `notifier_expirations_essai` est volontairement retiré
    // (il créait un second type de notification, donc un doublon).
    const fullName = metaNomComplet(user);
    const { prenom, nom } = decouperNom(fullName);

    const { data: etatData, error: etatError } = await supabase.rpc("compte_authentifie");
    if (etatError) return null;
    const etat = etatData as EtatCompteRpc | null;
    if (!etat?.authentifie) return null;

    // Compte suspendu / désactivé : on conserve l'identité pour afficher l'écran de blocage.
    if (!etat.compte_actif) {
      if (etat.raison === "compte_inactif" && etat.role && etat.statut) {
        // Pour un compte « en attente », la raison du blocage provient TOUJOURS de
        // l'offre de lancement : plus aucun message générique sans explication.
        const blocage =
          etat.statut === "en_attente" ? resoudreBlocageEssai(essai, essaiErreur) : null;
        return {
          id: "",
          email: user.email ?? "",
          prenom,
          nom,
          role: etat.role,
          statut: etat.statut,
          fonction: fonctionPourRole(etat.role),
          raisonBlocage: blocage?.raison,
          raisonCode: blocage?.code,
        };
      }
      return null;
    }

    const { data: profilData, error: profilError } = await supabase.rpc("mon_profil");
    if (profilError || !profilData) return null;
    const profil = profilData as MonProfilRpc;
    if (!profil.connecte || !profil.compte) return null;

    const nomCompletCompte = (profil.compte.nom_complet || fullName).trim();
    const nomDecoupe = decouperNom(nomCompletCompte);
    const rawPermissions = profil.employe?.permissions;
    const permissions =
      rawPermissions && typeof rawPermissions === "object" && !Array.isArray(rawPermissions)
        ? (rawPermissions as PermissionMap)
        : undefined;
    const scope: "agence" | "proprietaire" | undefined = profil.employe
      ? profil.employe.proprietaire_id
        ? "proprietaire"
        : "agence"
      : profil.agence
        ? "agence"
        : profil.proprietaire
          ? "proprietaire"
          : undefined;

    return {
      id: profil.compte.id,
      email: profil.compte.email ?? user.email ?? "",
      prenom: nomDecoupe.prenom,
      nom: nomDecoupe.nom,
      role: profil.compte.role,
      statut: profil.compte.statut,
      fonction: fonctionPourRole(profil.compte.role),
      agence: profil.agence?.nom,
      permissions,
      scope,
    };
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const storedSession = await getSafeSession();
        if (!active) return;
        const user = storedSession?.user ?? null;
        if (user) {
          const profile = await loadProfile(user);
          if (active) setSession(profile);
        }
      } catch {
        if (active) setSession(null);
      } finally {
        if (active) setLoading(false);
      }
    })();

    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      // Callback synchrone uniquement : on ne déclenche aucun appel réseau ici.
      if (event === "SIGNED_OUT") {
        setSession(null);
      }
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        return { ok: false, error: traduireErreurAuth(error.message) };
      }
      const profile = await loadProfile(data.user);
      if (!profile) {
        await supabase.auth.signOut();
        return { ok: false, error: "Aucun profil n'est associé à ce compte." };
      }
      setSession(profile);
      return { ok: true, user: profile };
    },
    [loadProfile]
  );

  const refresh = useCallback(async () => {
    const user = await getSafeUser();
    setSession(await loadProfile(user));
  }, [loadProfile]);

  const logout = useCallback(async () => {
    setSession(null);
    await supabase.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({ session, isAuthenticated: session !== null, loading, login, refresh, logout }),
    [session, loading, login, refresh, logout]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession doit être utilisé à l'intérieur de SessionProvider");
  }
  return context;
}